import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material';
import { trackGallerySwipe } from '../../services/analyticsService';

const getImageUrl = (image) => image?.imageUrl || image?.url || '';

const ImageLightbox = ({ open, images = [], startIndex = 0, onClose, providerId = null, sessionId = null }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState('center center');
  const touchStartX = useRef(null);

  useEffect(() => {
    if (open) {
      setCurrentIndex(startIndex);
      setZoomed(false);
      setZoomOrigin('center center');
    }
  }, [open, startIndex]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => {
      if (i >= images.length - 1) return i;
      setZoomed(false);
      if (providerId) trackGallerySwipe(providerId, 'next', sessionId);
      return i + 1;
    });
  }, [images.length, providerId, sessionId]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => {
      if (i <= 0) return i;
      setZoomed(false);
      if (providerId) trackGallerySwipe(providerId, 'prev', sessionId);
      return i - 1;
    });
  }, [providerId, sessionId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, goNext, goPrev, onClose]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) goNext();
    else goPrev();
  };

  const handleImageClick = (e) => {
    if (!zoomed) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomOrigin(`${x}% ${y}%`);
    }
    setZoomed((z) => !z);
  };

  if (!images.length) return null;

  const currentImage = images[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: '#000',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Fejléc sor */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          flexShrink: 0,
          zIndex: 2,
        }}
      >
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
          {currentIndex + 1} / {images.length}
        </Typography>
      </Box>

      {/* Fő képterület */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Kép */}
        <Box
          component="img"
          src={getImageUrl(currentImage)}
          alt={currentImage?.title || `Kép ${currentIndex + 1}`}
          onClick={handleImageClick}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            cursor: zoomed ? 'zoom-out' : 'zoom-in',
            transform: zoomed ? 'scale(2.5)' : 'scale(1)',
            transformOrigin: zoomOrigin,
            transition: 'transform 0.3s ease',
            userSelect: 'none',
            WebkitUserDrag: 'none',
          }}
        />


        {/* Navigációs gombok */}
        {images.length > 1 && (
          <>
            {currentIndex > 0 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                sx={{
                  position: 'absolute',
                  left: { xs: 4, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  zIndex: 1,
                }}
              >
                <ArrowBackIos sx={{ fontSize: 20, position: 'relative', left: 3 }} />
              </IconButton>
            )}
            {currentIndex < images.length - 1 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                sx={{
                  position: 'absolute',
                  right: { xs: 4, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  zIndex: 1,
                }}
              >
                <ArrowForwardIos sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </>
        )}
      </Box>

      {/* Kép felirata (pl. forrás megjelölés) - csak ha van megadva */}
      {currentImage?.description && (
        <Typography
          sx={{
            flexShrink: 0,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.7)',
            fontStyle: 'italic',
            fontSize: '0.75rem',
            px: 2,
            pb: 1,
          }}
        >
          {currentImage.description}
        </Typography>
      )}

      {/* Thumbnail sáv */}
      {images.length > 1 && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            overflowX: 'auto',
            px: 1,
            py: 1,
            gap: 0.75,
            justifyContent: 'center',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {images.map((image, index) => (
            <Box
              key={image.id ?? index}
              onClick={() => { setZoomed(false); setCurrentIndex(index); }}
              component="img"
              src={getImageUrl(image)}
              alt={image.title || `Miniatűr ${index + 1}`}
              sx={{
                flexShrink: 0,
                width: { xs: 52, sm: 68 },
                height: { xs: 40, sm: 52 },
                objectFit: 'cover',
                borderRadius: '4px',
                cursor: 'pointer',
                border: index === currentIndex ? '2px solid #D4A574' : '2px solid transparent',
                opacity: index === currentIndex ? 1 : 0.5,
                transition: 'border-color 0.2s, opacity 0.2s',
                '&:hover': { opacity: 1, borderColor: '#B8860B' },
              }}
            />
          ))}
        </Box>
      )}
    </Dialog>
  );
};

export default ImageLightbox;
